import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader, RowActions } from "@/components/admin/crud-helpers";
import { StatsStrip } from "@/components/admin/stats-strip";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { formatPrice } from "@/lib/storefront";
import { Package, LayoutGrid, Users, KeyRound, Search } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/support-plans")({
  head: () => ({ meta: [{ title: "Support plans — Admin" }] }),
  component: AdminSupportPlansPage,
});

type Plan = {
  id: string;
  name: string;
  slug: string;
  kind: "free" | "monthly" | "yearly" | "one_time";
  description: string | null;
  price_cents: number;
  currency: string;
  duration_days: number | null;
  ticket_quota: number | null;
  sla_hours: number | null;
  active: boolean;
  sort_order: number;
};
type Category = { id: string; name: string; slug: string; description: string | null; active: boolean; sort_order: number };
type PlanCat = { plan_id: string; category_id: string };
type Subscription = {
  id: string;
  user_id: string;
  plan_id: string;
  activation_key: string;
  status: "active" | "expired" | "cancelled";
  starts_at: string;
  expires_at: string | null;
  notes: string | null;
  created_at: string;
};

const kindLabel: Record<Plan["kind"], string> = {
  free: "Free",
  monthly: "Monthly",
  yearly: "Yearly",
  one_time: "One-time key",
};

function AdminSupportPlansPage() {
  const { data: plans = [] } = useQuery({
    queryKey: ["admin-support-plans"],
    queryFn: async () => (await supabase.from("support_plans").select("*").order("sort_order")).data as Plan[] ?? [],
  });
  const { data: cats = [] } = useQuery({
    queryKey: ["admin-support-cats"],
    queryFn: async () => (await supabase.from("support_categories").select("*").order("sort_order")).data as Category[] ?? [],
  });
  const { data: subs = [] } = useQuery({
    queryKey: ["admin-support-subs"],
    queryFn: async () => (await supabase.from("support_subscriptions").select("*").order("created_at", { ascending: false })).data as Subscription[] ?? [],
  });

  const activePlans = plans.filter((p) => p.active).length;
  const activeSubs = subs.filter((s) => s.status === "active").length;
  const activeCats = cats.filter((c) => c.active).length;

  return (
    <div className="space-y-4">
      <AdminPageHeader title="Support system" description="Plans, categories, and active subscriptions." />
      <StatsStrip
        tiles={[
          { label: "Plans", value: `${activePlans}/${plans.length}`, icon: Package, hint: "active / total", tone: "info" },
          { label: "Categories", value: `${activeCats}/${cats.length}`, icon: LayoutGrid, hint: "active / total" },
          { label: "Subscriptions", value: activeSubs, icon: Users, tone: activeSubs ? "success" : "default", hint: `of ${subs.length} total` },
          { label: "Cancelled", value: subs.filter((s) => s.status === "cancelled").length, icon: KeyRound, tone: "warning" },
        ]}
      />
      <Tabs defaultValue="plans">
        <PillTabsList search>
          <PillTabsTrigger value="plans">Plans</PillTabsTrigger>
          <PillTabsTrigger value="categories">Categories</PillTabsTrigger>
          <PillTabsTrigger value="access">Plan access</PillTabsTrigger>
          <PillTabsTrigger value="subs">Subscriptions</PillTabsTrigger>
        </PillTabsList>
        <TabsContent value="plans" className="mt-4"><PlansTab /></TabsContent>
        <TabsContent value="categories" className="mt-4"><CategoriesTab /></TabsContent>
        <TabsContent value="access" className="mt-4"><AccessTab /></TabsContent>
        <TabsContent value="subs" className="mt-4"><SubsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// -------------- PLANS --------------
function PlansTab() {
  const qc = useQueryClient();
  const { data: plans = [] } = useQuery({
    queryKey: ["admin-support-plans"],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_plans").select("*").order("sort_order");
      if (error) throw error;
      return data as Plan[];
    },
  });

  const [editing, setEditing] = useState<Partial<Plan> | null>(null);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search) return plans;
    const s = search.toLowerCase();
    return plans.filter((p) => p.name.toLowerCase().includes(s) || p.slug.toLowerCase().includes(s));
  }, [plans, search]);

  const save = useMutation({
    mutationFn: async (p: Partial<Plan>) => {
      const payload = {
        name: p.name!,
        slug: p.slug!,
        kind: p.kind!,
        description: p.description ?? null,
        price_cents: Number(p.price_cents ?? 0),
        currency: p.currency || "USD",
        duration_days: p.duration_days ?? null,
        ticket_quota: p.ticket_quota ?? null,
        sla_hours: p.sla_hours ?? null,
        active: p.active ?? true,
        sort_order: Number(p.sort_order ?? 0),
      };
      const { error } = p.id
        ? await supabase.from("support_plans").update(payload).eq("id", p.id)
        : await supabase.from("support_plans").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Saved");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["admin-support-plans"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("support_plans").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin-support-plans"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 sm:flex sm:flex-row sm:justify-between">
        <div className="min-w-0">
          <CardTitle>Plans</CardTitle>
          <CardDescription>Free, monthly, yearly, or one-time activation keys.</CardDescription>
        </div>
        <Button size="sm" className="shrink-0" onClick={() => setEditing({ kind: "monthly", active: true, sort_order: plans.length })}>
          New plan
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search plans…" className="pl-9" />
        </div>

        {/* Mobile: cards */}
        <div className="grid gap-2 md:hidden">
          {filtered.length === 0 && (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">No plans.</div>
          )}
          {filtered.map((p) => (
            <div key={p.id} className="rounded-lg border border-border bg-card p-3">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                <div className="min-w-0">
                  <div className="truncate font-medium">{p.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{p.slug}</div>
                </div>
                <RowActions onEdit={() => setEditing(p)} onDelete={() => { if (confirm("Delete this plan?")) del.mutate(p.id); }} />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                <Badge variant="outline">{kindLabel[p.kind]}</Badge>
                <Badge variant="secondary">{formatPrice(p.price_cents)}</Badge>
                {p.duration_days && <Badge variant="outline">{p.duration_days}d</Badge>}
                <Badge variant="outline">Quota: {p.ticket_quota ?? "∞"}</Badge>
                {p.sla_hours && <Badge variant="outline">SLA {p.sla_hours}h</Badge>}
                {p.active ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: table */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-2">Name</th><th>Kind</th><th>Price</th><th>Duration</th><th>Quota</th><th>SLA</th><th>Active</th><th />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">No plans match your search.</td></tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} className="border-b">
                  <td className="py-2 font-medium">{p.name}<div className="text-xs text-muted-foreground">{p.slug}</div></td>
                  <td><Badge variant="outline">{kindLabel[p.kind]}</Badge></td>
                  <td>{formatPrice(p.price_cents)}</td>
                  <td>{p.duration_days ? `${p.duration_days}d` : "—"}</td>
                  <td>{p.ticket_quota ?? "∞"}</td>
                  <td>{p.sla_hours ? `${p.sla_hours}h` : "—"}</td>
                  <td>{p.active ? <Badge>Yes</Badge> : <Badge variant="secondary">No</Badge>}</td>
                  <td><RowActions onEdit={() => setEditing(p)} onDelete={() => { if (confirm("Delete this plan?")) del.mutate(p.id); }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>


      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit plan" : "New plan"}</DialogTitle>
            <DialogDescription>Configure pricing, duration, and limits.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1"><Label>Name</Label><Input value={editing.name ?? ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div className="space-y-1"><Label>Slug</Label><Input value={editing.slug ?? ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
              <div className="space-y-1">
                <Label>Kind</Label>
                <Select value={editing.kind} onValueChange={(v) => setEditing({ ...editing, kind: v as Plan["kind"] })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(["free","monthly","yearly","one_time"] as const).map(k => <SelectItem key={k} value={k}>{kindLabel[k]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Price (cents)</Label><Input type="number" value={editing.price_cents ?? 0} onChange={(e) => setEditing({ ...editing, price_cents: Number(e.target.value) })} /></div>
              <div className="space-y-1"><Label>Duration (days)</Label><Input type="number" value={editing.duration_days ?? ""} onChange={(e) => setEditing({ ...editing, duration_days: e.target.value ? Number(e.target.value) : null })} /></div>
              <div className="space-y-1"><Label>Ticket quota</Label><Input type="number" value={editing.ticket_quota ?? ""} onChange={(e) => setEditing({ ...editing, ticket_quota: e.target.value ? Number(e.target.value) : null })} /></div>
              <div className="space-y-1"><Label>SLA hours</Label><Input type="number" value={editing.sla_hours ?? ""} onChange={(e) => setEditing({ ...editing, sla_hours: e.target.value ? Number(e.target.value) : null })} /></div>
              <div className="space-y-1"><Label>Sort order</Label><Input type="number" value={editing.sort_order ?? 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} /></div>
              <div className="sm:col-span-2 space-y-1"><Label>Description</Label><Textarea value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="sm:col-span-2 flex items-center gap-2"><Switch checked={editing.active ?? true} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><Label>Active</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => editing && save.mutate(editing)} disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// -------------- CATEGORIES --------------
function CategoriesTab() {
  const qc = useQueryClient();
  const { data: cats = [] } = useQuery({
    queryKey: ["admin-support-cats"],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_categories").select("*").order("sort_order");
      if (error) throw error;
      return data as Category[];
    },
  });
  const [editing, setEditing] = useState<Partial<Category> | null>(null);

  const save = useMutation({
    mutationFn: async (c: Partial<Category>) => {
      const payload = { name: c.name!, slug: c.slug!, description: c.description ?? null, active: c.active ?? true, sort_order: Number(c.sort_order ?? 0) };
      const { error } = c.id
        ? await supabase.from("support_categories").update(payload).eq("id", c.id)
        : await supabase.from("support_categories").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Saved"); setEditing(null); qc.invalidateQueries({ queryKey: ["admin-support-cats"] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("support_categories").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-support-cats"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div><CardTitle>Categories</CardTitle><CardDescription>Topics customers can pick when filing a ticket.</CardDescription></div>
        <Button size="sm" onClick={() => setEditing({ active: true, sort_order: cats.length })}>New category</Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b"><th className="py-2">Name</th><th>Slug</th><th>Active</th><th /></tr></thead>
            <tbody>
              {cats.map((c) => (
                <tr key={c.id} className="border-b">
                  <td className="py-2 font-medium">{c.name}{c.description && <div className="text-xs text-muted-foreground">{c.description}</div>}</td>
                  <td className="font-mono text-xs">{c.slug}</td>
                  <td>{c.active ? <Badge>Yes</Badge> : <Badge variant="secondary">No</Badge>}</td>
                  <td><RowActions onEdit={() => setEditing(c)} onDelete={() => { if (confirm("Delete this category?")) del.mutate(c.id); }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Name</Label><Input value={editing.name ?? ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div className="space-y-1"><Label>Slug</Label><Input value={editing.slug ?? ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
              <div className="space-y-1"><Label>Description</Label><Textarea value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="space-y-1"><Label>Sort order</Label><Input type="number" value={editing.sort_order ?? 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} /></div>
              <div className="flex items-center gap-2"><Switch checked={editing.active ?? true} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><Label>Active</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => editing && save.mutate(editing)} disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// -------------- ACCESS MATRIX --------------
function AccessTab() {
  const qc = useQueryClient();
  const { data: plans = [] } = useQuery({
    queryKey: ["admin-support-plans"],
    queryFn: async () => (await supabase.from("support_plans").select("*").order("sort_order")).data as Plan[] ?? [],
  });
  const { data: cats = [] } = useQuery({
    queryKey: ["admin-support-cats"],
    queryFn: async () => (await supabase.from("support_categories").select("*").order("sort_order")).data as Category[] ?? [],
  });
  const { data: links = [] } = useQuery({
    queryKey: ["admin-support-plan-cats"],
    queryFn: async () => (await supabase.from("support_plan_categories").select("*")).data as PlanCat[] ?? [],
  });

  const toggle = useMutation({
    mutationFn: async ({ plan_id, category_id, on }: PlanCat & { on: boolean }) => {
      if (on) {
        const { error } = await supabase.from("support_plan_categories").insert({ plan_id, category_id });
        if (error) throw error;
      } else {
        const { error } = await supabase.from("support_plan_categories").delete().eq("plan_id", plan_id).eq("category_id", category_id);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-support-plan-cats"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const has = (p: string, c: string) => links.some(l => l.plan_id === p && l.category_id === c);

  return (
    <Card>
      <CardHeader><CardTitle>Plan access matrix</CardTitle><CardDescription>Which plans can open tickets in which categories.</CardDescription></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b">
              <th className="py-2">Category</th>
              {plans.map(p => <th key={p.id} className="px-3 text-center">{p.name}</th>)}
            </tr></thead>
            <tbody>
              {cats.map(c => (
                <tr key={c.id} className="border-b">
                  <td className="py-2 font-medium">{c.name}</td>
                  {plans.map(p => (
                    <td key={p.id} className="px-3 text-center">
                      <Checkbox checked={has(p.id, c.id)} onCheckedChange={(v) => toggle.mutate({ plan_id: p.id, category_id: c.id, on: v === true })} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// -------------- SUBSCRIPTIONS --------------
function SubsTab() {
  const qc = useQueryClient();
  const { data: subs = [] } = useQuery({
    queryKey: ["admin-support-subs"],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_subscriptions").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data as Subscription[];
    },
  });
  const { data: plans = [] } = useQuery({
    queryKey: ["admin-support-plans"],
    queryFn: async () => (await supabase.from("support_plans").select("id,name,slug,kind").order("sort_order")).data as Pick<Plan, "id"|"name"|"slug"|"kind">[] ?? [],
  });

  const [granting, setGranting] = useState<{ user_id: string; plan_id: string; notes: string } | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filtered = useMemo(() => {
    return subs.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!s.user_id.toLowerCase().includes(q) && !s.activation_key.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [subs, search, statusFilter]);

  const grant = useMutation({
    mutationFn: async () => {
      if (!granting) return;
      const { error } = await supabase.from("support_subscriptions").insert({
        user_id: granting.user_id,
        plan_id: granting.plan_id,
        notes: granting.notes || null,
        activation_key: "", // trigger generates the real key
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Subscription granted — key auto-generated"); setGranting(null); qc.invalidateQueries({ queryKey: ["admin-support-subs"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Subscription["status"] }) => {
      const { error } = await supabase.from("support_subscriptions").update({
        status,
        cancelled_at: status === "cancelled" ? new Date().toISOString() : null,
      }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Updated"); qc.invalidateQueries({ queryKey: ["admin-support-subs"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const planName = (id: string) => plans.find(p => p.id === id)?.name ?? id.slice(0, 8);

  return (
    <Card>
      <CardHeader className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 sm:flex sm:flex-row sm:justify-between">
        <div className="min-w-0"><CardTitle>Subscriptions</CardTitle><CardDescription>Active customer support subscriptions. Keys auto-activate on purchase.</CardDescription></div>
        <Button size="sm" className="shrink-0" onClick={() => setGranting({ user_id: "", plan_id: plans[0]?.id ?? "", notes: "" })}>Grant subscription</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by user or key…" className="pl-9" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">

          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b">
              <th className="py-2">User</th><th>Plan</th><th>Key</th><th>Status</th><th>Expires</th><th />
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No subscriptions match.</td></tr>}
              {filtered.map(s => (

                <tr key={s.id} className="border-b">
                  <td className="py-2 font-mono text-xs">{s.user_id.slice(0, 8)}</td>
                  <td>{planName(s.plan_id)}</td>
                  <td className="font-mono text-xs">{s.activation_key}</td>
                  <td>
                    <Select value={s.status} onValueChange={(v) => updateStatus.mutate({ id: s.id, status: v as Subscription["status"] })}>
                      <SelectTrigger className="h-7 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(["active","expired","cancelled"] as const).map(st => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="text-xs text-muted-foreground">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "—"}</td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={!!granting} onOpenChange={(o) => !o && setGranting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Grant subscription</DialogTitle><DialogDescription>The activation key is generated and activated automatically.</DialogDescription></DialogHeader>
          {granting && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>User ID</Label><Input value={granting.user_id} onChange={(e) => setGranting({ ...granting, user_id: e.target.value })} placeholder="uuid" /></div>
              <div className="space-y-1"><Label>Plan</Label>
                <Select value={granting.plan_id} onValueChange={(v) => setGranting({ ...granting, plan_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Notes</Label><Textarea value={granting.notes} onChange={(e) => setGranting({ ...granting, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGranting(null)}>Cancel</Button>
            <Button onClick={() => grant.mutate()} disabled={grant.isPending || !granting?.user_id || !granting?.plan_id}>{grant.isPending ? "Granting…" : "Grant"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
